/**
 * Fusi IANA senza librerie, solo con Intl.DateTimeFormat.
 *
 * Convenzioni:
 *  - "giorno" = numero intero di giorni dall'epoca (1970-01-01 = 0) del calendario civile;
 *  - "wall" = millisecondi "come se l'ora locale fosse UTC" (Date.UTC dei campi locali);
 *  - gli istanti assoluti sono millisecondi epoch.
 */

const MS_DAY = 86_400_000;

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(tz: string): Intl.DateTimeFormat {
  let f = formatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatters.set(tz, f);
  }
  return f;
}

/** Lancia RangeError se il fuso non esiste. */
export function assertValidTimeZone(tz: string): void {
  formatter(tz);
}

/** Offset del fuso (ms, positivo a est di Greenwich) all'istante `instantMs`. */
export function tzOffsetMs(instantMs: number, tz: string): number {
  const floored = Math.floor(instantMs / 1000) * 1000;
  const parts = formatter(tz).formatToParts(new Date(floored));
  let y = 0, mo = 0, d = 0, h = 0, mi = 0, s = 0;
  for (const p of parts) {
    switch (p.type) {
      case 'year': y = Number(p.value); break;
      case 'month': mo = Number(p.value); break;
      case 'day': d = Number(p.value); break;
      case 'hour': h = Number(p.value) % 24; break; // alcune implementazioni danno "24" a mezzanotte
      case 'minute': mi = Number(p.value); break;
      case 'second': s = Number(p.value); break;
      default: break;
    }
  }
  return Date.UTC(y, mo - 1, d, h, mi, s) - floored;
}

/** Numero di giorno civile locale dell'istante. */
export function localDay(instantMs: number, tz: string): number {
  return Math.floor((instantMs + tzOffsetMs(instantMs, tz)) / MS_DAY);
}

/**
 * Wall-clock (giorno, secondi dalla mezzanotte) -> istante assoluto.
 *  - ora ripetuta (fine DST): prima occorrenza (offset più alto, fold=0);
 *  - ora inesistente (buco DST): si sposta in avanti della durata del buco
 *    ("compatible": si usa l'offset di PRIMA della transizione, 02:30 -> 03:30 CEST).
 */
export function localToInstant(day: number, secondsOfDay: number, tz: string): number {
  const wall = day * MS_DAY + secondsOfDay * 1000;
  // offset prima e dopo un'eventuale transizione vicina (±1 giorno: transizioni mai più ravvicinate)
  const oBefore = tzOffsetMs(wall - MS_DAY, tz);
  const oAfter = tzOffsetMs(wall + MS_DAY, tz);
  if (oBefore === oAfter) return wall - oBefore;
  const cBefore = wall - oBefore;
  const cAfter = wall - oAfter;
  const okBefore = tzOffsetMs(cBefore, tz) === oBefore;
  const okAfter = tzOffsetMs(cAfter, tz) === oAfter;
  if (okBefore && okAfter) return Math.min(cBefore, cAfter); // ripetuta: la prima
  if (okBefore) return cBefore;
  if (okAfter) return cAfter;
  return cBefore; // buco: offset precedente alla transizione
}

/** "YYYY-MM-DD" -> numero di giorno. */
export function parseDay(iso: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) throw new Error(`data non valida: ${iso}`);
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / MS_DAY;
}

/** "HH:MM:SS" -> secondi dalla mezzanotte. */
export function parseSeconds(iso: string): number {
  const m = /^(\d{2}):(\d{2}):(\d{2})$/.exec(iso);
  if (!m) throw new Error(`ora non valida: ${iso}`);
  return Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3]);
}

/** Giorno della settimana, 0 = lunedì. */
export function weekday(day: number): number {
  return (((day + 3) % 7) + 7) % 7; // 1970-01-01 era giovedì (3)
}

export function mondayOf(day: number): number {
  return day - weekday(day);
}

/** ISO8601 con offset esplicito (o Z) -> millisecondi epoch. */
export function parseInstant(value: number | string): number {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new Error('istante non valido');
    return value;
  }
  if (!/(Z|z|[+-]\d{2}:?\d{2})$/.test(value)) throw new Error(`istante senza offset: ${value}`);
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) throw new Error(`istante non valido: ${value}`);
  return ms;
}

export { MS_DAY };
